import { Component, inject } from '@angular/core';
import { BasePageComponent } from '../../../_shared/components/page/base-page.component';
import { AccountFilterData, WidgetFilterData } from '../../../_shared/models/filters/widget-filter-data.model';
import { AccountFilterComponent } from '../../../_shared/components/filters/account-filter/account-filter.component';
import { isEmptyObject } from '../../../_shared/helpers/object.helper';
import { FilterService } from '../../../_shared/services/filter.service';
import { UpcomingMaintenanceComponent } from "./components/widgets/upcoming-maintenance/upcoming-maintenance.component";
import { DisconnectedUnitsComponent } from './components/widgets/disconnected-units/disconnected-units.component';
import { MakesComponent } from './components/widgets/makes/makes.component';
import { ConnectedServiceLibrariesComponent } from './components/widgets/connected-service-libraries/connected-service-libraries.component';
import { ServiceLibraryService } from './services/service-library.service';
import { Subject } from 'rxjs';
import { ServiceSchedule } from './models/service-library.models';
import { ErrorHandlerService } from '../../../_shared/services/error-handler.service';
import { ScheduleType } from '../../_shared/enums/service-schedule-type.enum';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { UpsertServiceScheduleModalComponent } from './components/modals/upsert-service-schedule-modal/upsert-service-schedule-modal.component';
import { AccountFilterRequest } from '../../../_shared/models/account-filter.model';
import { ConfirmationOptions } from '../../../_shared/models/modals/confirmation-options.model';
import { ConfirmationModalService } from '../../../_shared/services/modals/confirmation-modal.service';
import { ToastrService } from 'ngx-toastr';
import { EmptyStateComponent } from '../../../_shared/components/states/empty-state/empty-state.component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-service-library',
  standalone: true,
  imports: [AccountFilterComponent, UpcomingMaintenanceComponent, DisconnectedUnitsComponent, MakesComponent, ConnectedServiceLibrariesComponent, EmptyStateComponent, TranslocoPipe],
  templateUrl: './service-library.component.html',
  styleUrl: './service-library.component.scss'
})
export class ServiceLibraryComponent extends BasePageComponent {
  private readonly transloco: TranslocoService = inject(TranslocoService);
  private filterService: FilterService = inject(FilterService);
  private serviceLibraryService: ServiceLibraryService = inject(ServiceLibraryService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private modalService: NgbModal = inject(NgbModal);
  private confirmationModalService = inject(ConfirmationModalService);
  private toastr: ToastrService = inject(ToastrService);

  isLoading: boolean = true;
  accountFilter: AccountFilterData;
  serviceSchedules: ServiceSchedule[] = [];

  private readonly cancelSubject = new Subject<void>();

  constructor() {
    super();
    this.accountFilter = {} as AccountFilterData;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  onAccountFilterChange(filter: AccountFilterData): void {
    this.accountFilter = filter;
    this.updateFilter();
  }

  updateFilter(): void {
    if (!isEmptyObject(this.accountFilter)) {
      this.filterService.updateFilter({
        ...this.accountFilter
      } as WidgetFilterData);

      this.updatePageFilterState();
      this.getServiceSchedulesAsync();
    }
  }

  getScheduleTypeName(serviceScheduleTypeId: ScheduleType): string {
    return this.transloco.translate(
      serviceScheduleTypeId === ScheduleType.Custom ? 'assetOverview.scheduleTypes.custom' : 'assetOverview.scheduleTypes.oem'
    );
  }

  canDeleteServiceSchedule(serviceScheduleTypeId: ScheduleType): boolean {
    return serviceScheduleTypeId === ScheduleType.Custom;
  }

  async getServiceSchedulesAsync(): Promise<void> {
    try {
      this.isLoading = true;
      const request: AccountFilterRequest = {
        accountIds: this.accountFilter.accountIds
      }
      this.cancelSubject.next();
      const response = await this.serviceLibraryService.getServiceSchedulesAsync(request, this.cancelSubject);
      this.serviceSchedules = response.data ?? [];
    } catch (error) {
      this.errorHandlerService.handleError(error)
    } finally {
      this.isLoading = false;
    }
  }

  onViewSchedule(schedule: ServiceSchedule) {
    this.router.navigate(['/asset-management/service-library/schedule', schedule.id], { state: { schedule: schedule } });
  }

  openCreateMaintenancePlanModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      windowClass: 'service-schedule-modal-window'
    };

    const modalRef = this.modalService.open(UpsertServiceScheduleModalComponent, modalOptions);
    modalRef.closed.subscribe(() => {
      this.getServiceSchedulesAsync();
    });
  }

  onDeleteSchedule(schedule: ServiceSchedule) {
    const options: ConfirmationOptions = {
      title: this.transloco.translate('serviceLibrary.delete.title'),
      message: this.transloco.translate('serviceLibrary.delete.message', { name: schedule.name }),
      warningMessage: this.transloco.translate('serviceLibrary.delete.warning'),
      confirmText: this.transloco.translate('serviceLibrary.delete.confirmText'),
      declineText: this.transloco.translate('serviceLibrary.delete.declineText'),
      confirmPlaceholder: this.transloco.translate('serviceLibrary.delete.confirmPlaceholder'),
      confirmationPrompt: 'delete',
      onConfirm: async () => {
        try {
          await this.serviceLibraryService.deleteServiceScheduleAsync(schedule.id);
          this.toastr.success(this.transloco.translate('serviceLibrary.delete.success'));
          this.getServiceSchedulesAsync();
        } catch (error) {
          this.errorHandlerService.handleError(error);
        }
      }
    };

    this.confirmationModalService.showConfirmationModal(options);
  }

  protected override onRefresh(): void { }
  protected override shouldRefresh(): boolean { return false; }
  protected override getFilters(): any { return {}; }
  protected override applyPageFilterState(pageFilter: any): void { }
}